import React, {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';

import styles from './TweetsColumnTabs.module.scss';
import { Grid } from '@material-ui/core';
import TweetColumn from '../TweetColumn';
import { apiRoutes as hashtagsApiRoutes } from 'hashtags/utils/apiRoutes';
import { DragDropContext } from 'react-beautiful-dnd';
import { changeTweetType } from 'hashtags/Requests';
import Loader from 'app/components/Loader';
import { toast } from 'react-toastify';
import { _t } from 'app/utils/i18n';
import { useMutation } from 'react-query';
import { checkTranslated } from 'app/utils/helpers';
import PopperQuestionMark from 'app/components/PopperQuestionMark';
import { flatMap } from 'app/utils/array-flat-map';
import { getTabNameFromSlug } from 'reports/pages/AddTemplate/constants';

interface Props {
  currentHashtag: any;
  compId: string;
  isReport?: boolean;
  showSlugs?: any;
  isTemplate?: boolean;
  reportState?: any;
  tabName?: string;
  filters?: any;
  isSample?: any;
}

const TweetsColumnTabs: FunctionComponent<Props> = ({
  currentHashtag,
  compId,
  isReport,
  showSlugs,
  isTemplate,
  reportState,
  tabName,
  filters,
  isSample,
}) => {
  const [activeTab, setActiveTab] = useState('');
  const [currentHashtags, setCurrentHashtags] = useState<any>(null);
  const { mutate, isLoading } = useMutation(changeTweetType);
  const openTab = (id: string) => setActiveTab(id);
  useEffect(() => {
    setCurrentHashtags(currentHashtag);
    setActiveTab(currentHashtag?.[0]?.id);
  }, [currentHashtag]);
  const currentHashtagTab = useMemo(() => {
    return currentHashtag?.find((hashtag: any) => hashtag.id === activeTab);
  }, [activeTab, currentHashtag]);

  const currentSlugs = showSlugs;

  if (!currentHashtags) return <Loader />;

  const onDrag = async ({ destination, draggableId }: any) => {
    if (currentHashtagTab?.status === 'ANALYZED') {
      if (!destination?.droppableId) {
        toast.error(_t('basic:invalid_area'));
        return;
      }

      const data = {
        category: destination?.droppableId?.toUpperCase(),
        hashtag_id: currentHashtagTab?.id,
        tweet_ids: [+draggableId],
      };
      mutate({ body: data });
    } else {
      toast.error(_t('basic:not_analyzed'));
    }
  };

  const getReq = (slug: string) =>
    slug?.includes('instagram')
      ? hashtagsApiRoutes().hashtagInstaPosts
      : slug?.includes('tiktok')
        ? hashtagsApiRoutes().hashtagTiktokPosts
        : slug?.includes('linkedin')
          ? hashtagsApiRoutes().hashtagLinkedInPosts
          : slug?.includes('youtube')
            ? hashtagsApiRoutes().hashtagVideos
            : hashtagsApiRoutes().hashtagTweets;

  if (isLoading)
    return (
      <div className="loader-holder ">
        <Loader />
      </div>
    );
  const renderPage = () => {
    const tweetsColumn = currentSlugs
      ?.filter(
        (ele: { item_id: string | number }) =>
          ele?.item_id === activeTab ||
          ele?.item_id === 0 ||
          !ele?.hasOwnProperty('item_id'),
      )
      .map((item: any, index: number) => ({
        id: item?.slug,
        component: (
          <Grid item lg={4} md={12}>
            <TweetColumn
              requestUrl={getReq(item?.slug)}
              type={
                item?.slug?.includes('negative')
                  ? 'negative'
                  : item?.slug?.includes('positive')
                    ? 'positive'
                    : item?.slug?.includes('neutral')
                      ? 'neutral'
                      : 'all'
              }
              tabName={getTabNameFromSlug(item?.slug)}
              customFilters={{
                hashtag_id: +item?.item_id,
                is_sample: isSample,
                limit: item?.item_count,
                order_column: item?.item_sort_type?.sort_colum,
                type_of_sort: item?.sort_type,
                ...filters,
              }}
              compId={`${item?.slug}_${index + 1}`}
              hideSort={true}
              isReport={true}
              hideQuestionmarkIcon={true}
              reportState={reportState}
              isTemplate={true}
            />
          </Grid>
        ),
      }));
    return flatMap(tweetsColumn)
      ?.filter((item) =>
        currentSlugs?.some((i: any) => i?.slug === item?.id)
          ? {
              ...item,
              order: currentSlugs.map((i: any) => i?.slug === item?.id)?.[0]
                ?.item_sort_type_id,
            }
          : false,
      )
      .sort((a, b) => a.order - b.order)

      .map((item) => item.component);
  };

  return (
    <div className={styles.TweetsColumnTabs} id={`${compId}-tweetsColumnTabs`}>
      <div className={styles.tweetsTabs} id={`${compId}-tweetsTabs`}>
        <div id={`${compId}-tweetsTabs-names`}>
          {currentHashtags && (
            <ul>
              {currentHashtags?.map((hash: any) => (
                <li
                  key={hash.id}
                  onClick={
                    () => openTab(hash.id) // hash.hashtag_name || hash.name)
                  }
                  className={hash.id == activeTab ? styles.active : ''}
                >
                  {checkTranslated(
                    hash.hashtag_name || hash.name,
                    hash.hashtag_name_translated || hash.name_translated,
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {!isReport && (
          <div className={styles.icons} id={`${compId}-tweetsTabs-Icon`}>
            <PopperQuestionMark compId={`${compId}_Tweets_column_tabs`} />
          </div>
        )}
      </div>
      <Grid container spacing={4} id={`${compId}-tweetsTabs-columns`}>
        <DragDropContext onDragEnd={onDrag}>{renderPage()}</DragDropContext>
      </Grid>
    </div>
  );
};

export default TweetsColumnTabs;
